import React, { useState } from "react";
import { getFirestore, doc, deleteDoc } from "firebase/firestore";
import moment from "moment";
import Modal from "react-modal";
import Swal from "sweetalert2";
import ModalCreateEvent from "./ModalCreateEvent"; // Modal de creación/edición

const VistaMes = ({
  events,
  currentDate,
  onSave,
  filters,
  handleAddCategory,
  refreshData,
  handleToday,
}) => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [modalEvents, setModalEvents] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [eventToEdit, setEventToEdit] = useState(null);

  const handleEditClick = (event) => {
    setEventToEdit({
      ...event,
      start: event.start ? moment(event.start).format("YYYY-MM-DDTHH:mm") : "",
    });
    setEditModalOpen(true);
    setModalOpen(false);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalEvents([]);
    setSelectedCategory(null);
  };

  const handleDeleteClick = async (event) => {
    // Calculamos el ancho real del scrollbar
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;

    // Si hay scrollbar, ajustamos paddingRight en función de su ancho
    if (scrollbarWidth > 0) {
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    } else {
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = "0";
    }

    const firestore = getFirestore();
    const confirm = await Swal.fire({
      title: "¿Eliminar evento?",
      text: "Esta acción no se puede deshacer.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });

    if (confirm.isConfirmed) {
      try {
        if (event.resource === "evento") {
          await deleteDoc(
            doc(firestore, "eventos", event.id.replace("evento-", ""))
          );
        } else if (event.resource === "tarea") {
          await deleteDoc(
            doc(firestore, "tareaCalendario", event.id.replace("tarea-", ""))
          );
        }
        Swal.fire(
          "Eliminado",
          "El evento ha sido eliminado con éxito.",
          "success"
        );

        setTimeout(() => {
          if (refreshData) refreshData();
          document.body.style.overflow = "auto";
          document.body.style.paddingRight = "0";
        }, 100);
      } catch (error) {
        console.error("Error al eliminar:", error);
        Swal.fire("Error", "No se pudo eliminar el evento.", "error");
        // Restauramos en caso de error
        document.body.style.overflow = "auto";
        document.body.style.paddingRight = "0";
      }
    } else {
      // Si se canceló el SweetAlert
      document.body.style.overflow = "auto";
      document.body.style.paddingRight = "0";
    }
  };

  const daysInMonth = currentDate.daysInMonth();
  const startOfMonth = (currentDate.clone().startOf("month").day() + 6) % 7;
  const daysArray = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const daysOfWeek = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

  const handleDayClick = (category, events) => {
    setSelectedCategory(category);
    setModalEvents(events);
    setModalOpen(true);
  };

  return (
    <>
      <div className="bg-gray-50 h-auto border border-gray-150 w-full">
        <div className="mx-auto bg-white max-w-[1600px] w-full overflow-hidden">
          {/* Encabezado de los días de la semana */}
          <div className="grid grid-cols-7 text-xs text-gray-500 text-center border-b p-2">
            {daysOfWeek.map((day) => (
              <div key={day}>{day}</div>
            ))}
          </div>
          {/* Días del mes */}
          <div className="grid grid-cols-7 text-sm">
            {Array.from({ length: startOfMonth }).map((_, idx) => (
              <div key={idx} className="h-28 border-b border-r"></div>
            ))}
            {daysArray.map((day) => {
              const isToday =
                currentDate.year() === moment().year() &&
                currentDate.month() === moment().month() &&
                day === parseInt(moment().format("D"), 10);
              const dayEvents = events.filter((event) =>
                moment(event.start).isSame(currentDate.clone().date(day), "day")
              );
              const cotizaciones = dayEvents.filter(
                (event) => event.resource === "cotizacion"
              );
              const eventos = dayEvents.filter(
                (event) => event.resource === "evento"
              );
              const festivos = dayEvents.filter(
                (event) => event.resource === "festivo"
              );
              const tareas = dayEvents.filter(
                (event) => event.resource === "tarea"
              );
              const hasMultipleEvents =
                cotizaciones.length + eventos.length + festivos.length > 1;

              return (
                <div
                  key={day}
                  className={`relative h-28 border-b border-r p-2 flex flex-col ${
                    hasMultipleEvents
                      ? "justify-start mt-0"
                      : "justify-end mt-auto"
                  }`}
                >
                  <span
                    className={`text-xs font-semibold absolute top-1 right-1 flex items-center justify-center w-6 h-6 ${
                      isToday
                        ? "bg-blue-500 text-white rounded-full"
                        : "text-gray-700"
                    }`}
                  >
                    {day}
                  </span>
                  <div className="flex flex-col gap-2 items-center">
                    {cotizaciones.length > 0 && (
                      <button
                        className="px-3 py-1 w-[120px] text-xs bg-blue-100 text-blue-500 rounded-sm hover:bg-blue-200 flex justify-center items-center"
                        onClick={() =>
                          handleDayClick("cotizacion", cotizaciones)
                        }
                      >
                        <i className="bi bi-file-earmark-text mr-1"></i>
                        Cotizaciones
                      </button>
                    )}
                    {eventos.length > 0 && (
                      <button
                        className="px-3 py-1 w-[120px] text-xs bg-green-100 text-green-500 rounded-sm hover:bg-green-200 flex justify-center items-center"
                        onClick={() => handleDayClick("evento", eventos)}
                      >
                        <i className="bi bi-calendar-event mr-1"></i>
                        Eventos
                      </button>
                    )}
                    {festivos.length > 0 && (
                      <button
                        className="px-3 py-1 w-[120px] text-xs bg-red-100 text-red-500 rounded-sm hover:bg-red-200 flex justify-center items-center"
                        onClick={() => handleDayClick("festivo", festivos)}
                      >
                        <i className="bi bi-star mr-1"></i>
                        Festivos
                      </button>
                    )}
                    {tareas.length > 0 &&
                      tareas.map((tarea, i) => {
                        const colorMapping = {
                          "#e0e7ff": "#6366F1",
                          "#ede9fe": "#7C3AED",
                          "#bae6fd": "#0284C7",
                          "#a7f3d0": "#059669",
                          "#fecaca": "#DC2323",
                          "#fef9c3": "#A16207",
                        };
                        const getDarkerColor = (hex) => {
                          if (colorMapping[hex]) return colorMapping[hex];
                          let r = parseInt(hex.substring(1, 3), 16) * 0.8;
                          let g = parseInt(hex.substring(3, 5), 16) * 0.8;
                          let b = parseInt(hex.substring(5, 7), 16) * 0.8;
                          return `rgb(${Math.max(
                            0,
                            Math.min(255, r)
                          )}, ${Math.max(0, Math.min(255, g))}, ${Math.max(
                            0,
                            Math.min(255, b)
                          )})`;
                        };
                        return (
                          <button
                            key={i}
                            className="px-3 py-1 w-[120px] text-xs rounded-sm flex justify-center items-center transition-colors duration-200 hover:brightness-90 overflow-hidden whitespace-nowrap"
                            style={{
                              textOverflow: "ellipsis",
                              backgroundColor: tarea.color,
                              color: getDarkerColor(tarea.color),
                              fontWeight: "400",
                              textShadow: "0px 1px 2px rgba(0,0,0,0.2)",
                            }}
                            onClick={() => handleDayClick("tareas", tareas)}
                          >
                            <i className="bi bi-rocket-takeoff mr-1"></i>
                            <span className="truncate">{tarea.category}</span>
                          </button>
                        );
                      })}
                  </div>
                </div>
              );
            })}
            {Array.from({
              length: 7 - ((startOfMonth + daysInMonth) % 7 || 7),
            }).map((_, idx) => (
              <div key={idx} className="h-28 border-b"></div>
            ))}
          </div>
        </div>
      </div>

      {/* Modal con la lista de eventos del día */}
      <Modal
        isOpen={modalOpen}
        onRequestClose={closeModal}
        contentLabel="Eventos del Día"
        style={{
          overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            zIndex: 101,
          },
          content: {
            top: "50%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            marginRight: "-50%",
            transform: "translate(-50%, -50%)",
            maxWidth: "400px",
            width: "90%",
            borderRadius: "4px",
            padding: "20px",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.3)",
            border: "none",
            backgroundColor: "#fff",
            fontFamily: "'Roboto', sans-serif",
          },
        }}
      >
        <div>
          <h3 className="text-lg font-bold text-center mb-4 text-gray-800">
            {modalEvents.length > 0
              ? moment(modalEvents[0].start).format("DD MMMM YYYY")
              : ""}
          </h3>
          <div style={{ textAlign: "center" }}>
            {modalEvents.map((event) => (
              <div
                key={event.id}
                style={{
                  background: event.color,
                  padding: "0.5rem",
                  borderRadius: "4px",
                  marginBottom: "10px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <i
                    className={`bi ${
                      selectedCategory === "cotizacion"
                        ? "bi-file-earmark-text"
                        : selectedCategory === "evento"
                        ? "bi-calendar-event"
                        : selectedCategory === "festivo"
                        ? "bi-star"
                        : "bi-rocket-takeoff"
                    } text-xl`}
                    style={{ alignSelf: "flex-start" }}
                  />
                  <span
                    style={{
                      wordWrap: "break-word",
                      whiteSpace: "normal",
                      overflowWrap: "break-word",
                      maxWidth: "90%",
                      display: "block",
                      textAlign: "justify",
                    }}
                  >
                    {event.title}
                  </span>
                </div>

                {/* Botones de Editar/Eliminar al final */}
                {(event.resource === "evento" || event.resource === "tarea") && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      marginTop: "10px",
                    }}
                  >
                    <button
                      onClick={() => handleEditClick(event)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        padding: "5px 10px",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#fff",
                        backgroundColor: "#007bff",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        minWidth: "80px",
                        justifyContent: "center",
                        marginRight: "8px",
                      }}
                    >
                      <i
                        className="bi bi-pencil-square"
                        style={{ fontSize: "14px" }}
                      ></i>
                      Editar
                    </button>
                    <button
                      onClick={() => handleDeleteClick(event)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        padding: "5px 10px",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#fff",
                        backgroundColor: "#DC2626",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        minWidth: "80px",
                        justifyContent: "center",
                      }}
                    >
                      <i
                        className="bi bi-trash"
                        style={{ fontSize: "14px" }}
                      ></i>
                      Eliminar
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          <button
            onClick={closeModal}
            className="modal-close"
            aria-label="Cerrar modal"
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>
      </Modal>

      {/* Modal para editar/crear evento */}
      <ModalCreateEvent
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        initialData={eventToEdit}
        onSave={onSave}
        categories={filters}
        onAddCategory={handleAddCategory}
      />
    </>
  );
};

export default VistaMes;
